import React from 'react'
import styled from '@emotion/styled'
import { Divider } from '@mui/material'
import { Skeleton } from '@mui/material'
import { browserLogger } from 'src/utils/browser-logger'
import { HelpTooltip } from 'src/components/help-tooltip'
import { Colors, Shapes } from 'src/components/theme'
import { Typography } from '@mui/material'
import { useParsedRouter } from 'src/layout/hooks/use-parsed-router'
import { GenericLoader, LoaderRow } from 'src/v2/components/skeleton-loaders'
import { FlexColumn } from 'src/components/flex-containers'

type PanelProps = {
  title?: React.ReactNode
  titleTooltipFile?: string
  subtitle?: string
  children?: React.ReactNode
  disableGutters?: boolean
  actions?: React.ReactNode
  noPadding?: boolean
  style?: React.CSSProperties
}

const StyledSkeleton = styled(Skeleton)`
  background-color: #fafafa;
`

const PanelWrapper = styled.div<{ noPadding?: boolean }>`
  padding-top: ${(props) => (props.noPadding ? 0 : Shapes.spacing[4])}px;
  padding-bottom: 0;
  padding-right: 0;
  padding-left: 0;
`

export const TitleWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 0 ${Shapes.spacing[4]}px;
  margin-bottom: ${Shapes.spacing[2]}px;
`

const TitleLeft = styled.div``

const TitleRight = styled.div`
  cursor: pointer;
`

export const Panel: React.FC<PanelProps> = (props) => {
  const { style, title, titleTooltipFile, subtitle, children, disableGutters, actions, noPadding } = props

  return (
    <>
      <PanelWrapper style={style} noPadding={noPadding}>
        {title &&
          (typeof title === 'string' ? (
            <TitleWrapper>
              <HelpTooltip file={titleTooltipFile} placement="bottom">
                <TitleLeft>
                  <Typography variant="textmd" color={Colors.neutral700}>
                    {title}
                  </Typography>
                  {subtitle && (
                    <div style={{ paddingTop: 4 }}>
                      <Typography variant="text2m" color={Colors.textMedium}>
                        {subtitle}
                      </Typography>
                    </div>
                  )}
                </TitleLeft>
              </HelpTooltip>
              {actions && <TitleRight>{actions}</TitleRight>}
            </TitleWrapper>
          ) : (
            title
          ))}
        {!disableGutters && children}
      </PanelWrapper>
      {disableGutters && children}
    </>
  )
}

type SeeAllProps = {
  insightModule: string
  title: string
}

export const SeeAll: React.FC<SeeAllProps> = (props) => {
  const { insightModule, title } = props
  const { router, query } = useParsedRouter()

  const onSeeAllClick = () => {
    void router.push({
      pathname: `/insights/${insightModule}`,
      query: query,
    })
  }
  const seeAll = (
    <Typography variant="text1m" color={Colors.blueDark} onClick={onSeeAllClick}>
      {title}
    </Typography>
  )
  return seeAll
}

type LoadablePanelProps = {
  title?: React.ReactNode
  titleTooltipFile?: string
  subtitle?: string
  height?: number
  actions?: React.ReactNode
  noPadding?: boolean
  children?: React.ReactNode
  marginTop?: number
}

type LoadingPanelProps = {
  title?: React.ReactNode
  titleTooltipFile?: string
  height?: number
  noPadding?: boolean
}

export const LoadingPanel: React.FC<LoadingPanelProps> = (props) => {
  const { title, titleTooltipFile, height, noPadding } = props
  return (
    <Panel title={title} titleTooltipFile={titleTooltipFile} noPadding={noPadding}>
      <div style={{ padding: '0px 8px' }}>
        <StyledSkeleton variant="rectangular" width="100%" height={height} />
      </div>
    </Panel>
  )
}

type LoadingInsightsPanelProps = {
  title?: React.ReactNode
  titleTooltipFile?: string
  rows?: number
  noPadding?: boolean
}

const LoadingInsightsPanelContents = styled(FlexColumn)`
  padding-inline: 24px;
`

const InsightTabLoader = () => {
  return (
    <LoaderRow alignItems="center" justifyContent="space-between" gap={Shapes.spacing[4]}>
      <LoaderRow alignItems="center" gap={12}>
        <GenericLoader color="normal" variant="rounded" width={30} height={30} animation="wave" />
        <GenericLoader
          color="light"
          variant="rounded"
          width={10 * (Math.floor(Math.random() * (15 - 12 + 1)) + 12)}
          height={12}
          animation="wave"
        />
      </LoaderRow>
      <GenericLoader color="light" variant="rounded" width={12} height={12} animation="wave" />
    </LoaderRow>
  )
}

export const LoadingInsightsPanel: React.FC<LoadingInsightsPanelProps> = (props) => {
  const { title, titleTooltipFile, rows, noPadding } = props

  return (
    <Panel title={title} titleTooltipFile={titleTooltipFile} noPadding={noPadding}>
      <LoadingInsightsPanelContents gap={Shapes.spacing[2]}>
        {Array.from({ length: rows || 4 }).map((_, i) => (
          <InsightTabLoader key={i} />
        ))}
      </LoadingInsightsPanelContents>
    </Panel>
  )
}

class ErrorEraser extends React.Component<any, { caught: boolean }> {
  state = {
    caught: false,
  }

  componentDidCatch(err: Error) {
    // TODO(bmc): log to backend?
    browserLogger.error(err)
    this.setState({ caught: true })
  }

  render() {
    if (this.state.caught) {
      return null
    }
    return this.props.children
  }
}

export const LoadablePanel: React.FC<LoadablePanelProps> = (props) => {
  const { title, titleTooltipFile, height, marginTop, children, noPadding } = props

  return (
    <div style={{ minHeight: height, marginTop }}>
      <ErrorEraser>
        <React.Suspense
          fallback={
            <LoadingPanel title={title} titleTooltipFile={titleTooltipFile} height={height} noPadding={noPadding} />
          }>
          <Panel title={title} titleTooltipFile={titleTooltipFile} noPadding={noPadding}>
            {children}
          </Panel>
        </React.Suspense>
        <Divider variant="fullWidth" />
      </ErrorEraser>
    </div>
  )
}
